import { readCard } from '@shared/card-markdown'
import { type PageRect } from '@shared/board'
import { type MaterialText } from '@shared/material'
import { matcher, readableLine } from '@shared/search'
import { clsx } from 'clsx'
import { BookOpen, FileText, MessageSquare, Scissors, Search } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useI18n } from '@/i18n/i18n'
import { isComposing } from '@/lib/ime'
import { loadMaterialText, useMaterials } from '@/materials/hooks'
import { useBoardCtx } from './context'

// ⌘K: find words on the board and in its materials, and go there. The same
// matching as the AI's `search` tool (shared/search.ts).

type Hit =
  | { kind: 'card' | 'message' | 'clip'; id: string; title: string; line: string; score: number }
  | {
      kind: 'page'
      materialId: string
      page: number
      rect: PageRect
      title: string
      line: string
      score: number
    }

const MAX_HITS = 40

export function SearchPalette() {
  const { t } = useI18n()
  const { client, canvas, actions, reader, boardId } = useBoardCtx()
  const board = useSyncExternalStore(client.subscribe, client.getSnapshot)
  const materials = useMaterials()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const [texts, setTexts] = useState<Record<string, MaterialText | null>>({})
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isComposing(e)) return
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey, { capture: true })
    return () => {
      window.removeEventListener('keydown', onKey, { capture: true })
    }
  }, [])

  // The materials' text, loaded the first time the palette opens.
  useEffect(() => {
    if (!open) return
    let gone = false
    for (const m of materials) {
      if (m.id in texts) continue
      void loadMaterialText(boardId, m).then((text) => {
        if (!gone) setTexts((cur) => ({ ...cur, [m.id]: text }))
      })
    }
    return () => {
      gone = true
    }
  }, [open, materials, boardId, texts])

  const hits = useMemo<Hit[]>(() => {
    if (!open || !query.trim()) return []
    const score = matcher(query)
    const out: Hit[] = []
    const best = (text: string) => {
      let top = { score: 0, line: '' }
      for (const line of text.split('\n')) {
        const s = score(line)
        if (s > top.score) top = { score: s, line }
      }
      return top
    }
    for (const i of board.state.items.values()) {
      if (i.kind === 'card') {
        const md = readCard(client.doc, i.id)
        const b = best(md)
        if (b.score)
          out.push({
            kind: 'card',
            id: i.id,
            title: readableLine(md.split('\n')[0] ?? ''),
            line: readableLine(b.line),
            score: b.score,
          })
      } else if (i.kind === 'message') {
        const b = best(i.data.text)
        if (b.score)
          out.push({ kind: 'message', id: i.id, title: '', line: b.line, score: b.score })
      } else if (i.kind === 'clip') {
        const b = best(i.data.text ?? '')
        if (b.score)
          out.push({
            kind: 'clip',
            id: i.id,
            title: `p.${i.data.page}`,
            line: b.line,
            score: b.score,
          })
      }
    }
    for (const m of materials) {
      texts[m.id]?.pages.forEach((lines, n) => {
        for (const l of lines) {
          const s = score(l.t)
          if (s)
            out.push({
              kind: 'page',
              materialId: m.id,
              page: n + 1,
              rect: { x: l.x, y: l.y, w: l.w, h: l.h },
              title: `${m.name} · p.${n + 1}`,
              line: l.t,
              score: s,
            })
        }
      })
    }
    // Best first; on the board before in the materials when equal.
    return out.sort((a, b) => b.score - a.score).slice(0, MAX_HITS)
  }, [open, query, board.state, client.doc, materials, texts])

  const close = () => {
    setOpen(false)
    setQuery('')
    setActive(0)
  }

  const go = (h: Hit) => {
    close()
    if (h.kind === 'page') {
      reader.open(h.materialId, h.page, h.rect)
      return
    }
    void canvas.reveal().then(() => {
      actions.current?.showItems([h.id])
    })
  }

  if (!open) return null
  const icon = { card: FileText, message: MessageSquare, clip: Scissors, page: BookOpen }
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/20 px-4 pt-[12vh]"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) close()
      }}
    >
      <div
        role="dialog"
        aria-label={t('search.label')}
        className="w-full max-w-xl overflow-hidden rounded-xl border border-line bg-surface shadow-2xl"
      >
        <div className="flex items-center gap-2 border-b border-line px-3">
          <Search size={16} className="flex-none text-muted" />
          <input
            ref={input}
            autoFocus
            value={query}
            placeholder={t('search.placeholder')}
            aria-label={t('search.label')}
            onChange={(e) => {
              setQuery(e.target.value)
              setActive(0)
            }}
            onKeyDown={(e) => {
              if (isComposing(e)) return
              if (e.key === 'Escape') close()
              else if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActive((a) => Math.min(a + 1, hits.length - 1))
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActive((a) => Math.max(a - 1, 0))
              } else if (e.key === 'Enter') {
                const h = hits[active]
                if (h) go(h)
              }
            }}
            className="h-12 min-w-0 flex-1 bg-transparent text-[14px] outline-none placeholder:text-muted"
          />
        </div>
        {query.trim() && (
          <ul
            role="listbox"
            aria-label={t('search.results')}
            className="max-h-[50vh] overflow-y-auto py-1"
          >
            {hits.length === 0 && (
              <li className="px-4 py-3 text-[13px] text-muted">{t('search.none')}</li>
            )}
            {hits.map((h, i) => {
              const Icon = icon[h.kind]
              return (
                <li
                  key={h.kind === 'page' ? `${h.materialId}:${h.page}:${h.rect.y}` : h.id}
                  role="option"
                  aria-selected={i === active}
                  onPointerEnter={() => {
                    setActive(i)
                  }}
                  onClick={() => {
                    go(h)
                  }}
                  className={clsx(
                    'flex cursor-pointer items-start gap-2.5 px-4 py-2 text-[13px]',
                    i === active && 'bg-surface-2',
                  )}
                >
                  <Icon size={14} className="mt-0.5 flex-none text-muted" />
                  <span className="min-w-0 flex-1">
                    {h.title && <span className="block truncate font-medium">{h.title}</span>}
                    <span className="block truncate text-muted">{h.line}</span>
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
